// MATReferralCard — surfaces when the deterministic clinical engine
// flags MAT/inpatient as appropriate. No AI involvement in the
// trigger decision; the body text is static.
//
// Three primary calls-to-action:
//   1. SAMHSA practitioner locator (find a buprenorphine clinician)
//   2. Telehealth options (curated short list)
//   3. SAMHSA national helpline 1-800-662-4357 (24/7)
//
// We intentionally don't recommend a specific drug or dose — the
// pathway is "talk to a clinician about MAT," not "take buprenorphine."

import { Card } from '../ui/Card';
import { Button } from '../ui/Button';

export interface MATRecommendation {
  matSuggested: boolean;
  inpatientSuggested: boolean;
  reasons: string[];
}

interface MATReferralCardProps {
  rec: MATRecommendation;
  onDismiss?: () => void;
  /**
   * Optional adaptive-guidance paragraph from /api/ai/adaptive-latest.
   * Renders ABOVE the deterministic reasons list with a small header
   * ("From the last week's pattern:"). Descriptive language only —
   * see prompts/adaptive.ts for the regex guard.
   */
  adaptiveGuidance?: string;
  /** ISO timestamp of the adaptive_guidance row. Reserved for future
   *  "last updated" disclosure. */
  adaptiveGeneratedAt?: string;
}

export function MATReferralCard({
  rec,
  onDismiss,
  adaptiveGuidance,
  adaptiveGeneratedAt: _adaptiveGeneratedAt,
}: MATReferralCardProps) {
  if (!rec.matSuggested && !rec.inpatientSuggested) return null;
  const titleId = 'mat-referral-title';
  const guidanceId = 'mat-referral-guidance';
  const hasGuidance = !!adaptiveGuidance && adaptiveGuidance.trim().length > 0;

  return (
    <Card accentLeft>
      <h3
        id={titleId}
        className="text-h3"
        style={{ marginBlockEnd: 'var(--space-2)' }}
        aria-describedby={hasGuidance ? guidanceId : undefined}
      >
        Worth a clinician conversation
      </h3>
      <p style={{ marginBlockEnd: 'var(--space-3)' }}>
        The last week or two of your check-ins suggest withdrawal is rougher than what a self-managed taper usually handles. Many people in this stretch find that talking to a clinician — particularly about buprenorphine/naloxone (Suboxone) — changes the trajectory.
      </p>
      {hasGuidance ? (
        <div
          id={guidanceId}
          style={{
            borderInlineStart: '2px solid var(--color-accent)',
            paddingInlineStart: 'var(--space-3)',
            marginBlockEnd: 'var(--space-3)',
          }}
        >
          <span
            className="text-micro"
            style={{
              color: 'var(--color-ink-tertiary)',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            From the last week's pattern
          </span>
          <p
            className="text-caption muted"
            style={{
              fontStyle: 'italic',
              marginBlockStart: 'var(--space-1)',
              lineHeight: 'var(--leading-normal)',
            }}
          >
            {adaptiveGuidance}
          </p>
        </div>
      ) : null}
      <ul
        style={{
          paddingInlineStart: 'var(--space-5)',
          margin: 0,
          marginBlockEnd: 'var(--space-3)',
          color: 'var(--color-ink-secondary)',
        }}
      >
        {rec.reasons.map((r) => (
          <li key={r} style={{ marginBlockEnd: 'var(--space-1)', lineHeight: 1.5 }}>
            {r}
          </li>
        ))}
      </ul>

      <div className="stack" style={{ '--stack-gap': 'var(--space-2)' } as React.CSSProperties}>
        <Button
          variant="primary"
          onClick={() =>
            window.open(
              'https://www.samhsa.gov/medications-substance-use-disorders/find-practitioner',
              '_blank',
              'noopener,noreferrer',
            )
          }
        >
          Find a buprenorphine clinician (SAMHSA)
        </Button>
        <Button
          variant="secondary"
          onClick={() =>
            window.open(
              'https://www.samhsa.gov/find-help/national-helpline',
              '_blank',
              'noopener,noreferrer',
            )
          }
        >
          SAMHSA helpline · 1-800-662-4357 (24/7)
        </Button>
        {rec.inpatientSuggested ? (
          <p
            className="text-caption"
            style={{
              color: 'var(--color-warning)',
              marginBlockStart: 'var(--space-2)',
              lineHeight: 1.5,
            }}
          >
            Your SOWS scores have been very high for several days in a row. Inpatient detox is a reasonable next step — call the SAMHSA helpline above to find programs in your area, or go to an ER if you're not safe waiting.
          </p>
        ) : null}
      </div>

      <p
        className="text-caption muted"
        style={{
          marginBlockStart: 'var(--space-3)',
          fontStyle: 'italic',
        }}
      >
        This card is rule-driven from your check-ins — not medical advice. It will keep showing until your scores ease.
      </p>

      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          style={{
            marginBlockStart: 'var(--space-2)',
            background: 'none',
            border: 0,
            color: 'var(--color-ink-tertiary)',
            fontSize: 'var(--text-caption)',
            textDecoration: 'underline',
            textUnderlineOffset: '0.2em',
            cursor: 'pointer',
            alignSelf: 'flex-start',
          }}
        >
          Dismiss for today
        </button>
      ) : null}
    </Card>
  );
}
